import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { shareViewerApi } from '@/services/share-api';
import type { ShareMeta, SharedDashboardData } from '@/services/share-api';
import { useLivePolling } from './useLivePolling';
import type {
  DashboardBlock,
  DashboardControl,
  RenderHandle,
  ControlValues,
  ShareSettings,
  DataMode,
} from '@/types/dashboard';

type ViewerPhase = 'loading' | 'pin_required' | 'ready' | 'error' | 'expired';

interface SharedDashboard {
  id: string;
  name: string;
  description?: string | null;
  data_mode: DataMode;
  refresh_interval?: number | null;
  theme: string;
  layout_columns: number;
  share_settings: ShareSettings;
}

interface UseSharedDashboardReturn {
  phase: ViewerPhase;
  dashboard: SharedDashboard | null;
  blocks: DashboardBlock[];
  blockData: Map<string, Record<string, unknown>[]>;
  controls: DashboardControl[];
  activeControlValues: Record<string, unknown>;
  isRefreshing: boolean;
  error: string | null;
  pinRequired: boolean;
  pinError: string | null;
  renderHandles: React.MutableRefObject<Map<string, RenderHandle>>;
  submitPin: (pin: string) => Promise<void>;
  changeControlValue: (parameterKey: string, value: unknown) => Promise<void>;
  refreshData: () => Promise<void>;
  lastRefreshedAt: Date | null;
  isPolling: boolean;
}

function buildControlValues(
  controlVals: Record<string, unknown>
): ControlValues | undefined {
  if (Object.keys(controlVals).length === 0) return undefined;

  const cv: ControlValues = {};
  for (const [, value] of Object.entries(controlVals)) {
    if (
      value &&
      typeof value === 'object' &&
      'type' in (value as Record<string, unknown>)
    ) {
      const tr = value as Record<string, unknown>;
      const trType = tr.type as string;
      if (['last_n_days', 'last_n_weeks', 'specific_range'].includes(trType)) {
        cv.time_range = value as ControlValues['time_range'];
      }
    }
  }
  return Object.keys(cv).length > 0 ? cv : undefined;
}

function sortBlocksByPosition(blocks: DashboardBlock[]): DashboardBlock[] {
  return [...blocks].sort((a, b) => {
    if (a.position.y !== b.position.y) return a.position.y - b.position.y;
    return a.position.x - b.position.x;
  });
}

export function useSharedDashboard(
  shareToken: string | undefined
): UseSharedDashboardReturn {
  const [phase, setPhase] = useState<ViewerPhase>('loading');
  const [dashboard, setDashboard] = useState<SharedDashboard | null>(null);
  const [blocks, setBlocks] = useState<DashboardBlock[]>([]);
  const [controls, setControls] = useState<DashboardControl[]>([]);
  const [blockData, setBlockData] = useState<
    Map<string, Record<string, unknown>[]>
  >(new Map());
  const [activeControlValues, setActiveControlValues] = useState<
    Record<string, unknown>
  >({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  const renderHandles = useRef<Map<string, RenderHandle>>(new Map());
  const shareTokenRef = useRef(shareToken);
  shareTokenRef.current = shareToken;

  const applyDashboardData = useCallback((data: SharedDashboardData) => {
    setDashboard(data.dashboard as SharedDashboard);

    const accepted = sortBlocksByPosition(
      (data.blocks as unknown as DashboardBlock[]).filter(b => b.status === 'accepted')
    );
    setBlocks(accepted);
    setControls(
      ([...data.controls] as unknown as DashboardControl[]).sort(
        (a, b) => a.position - b.position
      )
    );

    const newData = new Map<string, Record<string, unknown>[]>();
    for (const [blockId, rows] of Object.entries(data.block_data)) {
      newData.set(blockId, rows);
    }
    setBlockData(newData);
  }, []);

  // Initial load — check meta then load dashboard
  useEffect(() => {
    if (!shareToken) return;

    let cancelled = false;

    async function init(): Promise<void> {
      setPhase('loading');
      setError(null);
      setPinError(null);

      try {
        const meta: ShareMeta = await shareViewerApi.getMeta(shareToken!);
        if (cancelled) return;

        if (meta.expired) {
          setPhase('expired');
          return;
        }

        if (meta.pin_required) {
          // Check for existing session token
          const existingToken = shareViewerApi.getSessionToken(shareToken!);
          if (existingToken) {
            try {
              const data = await shareViewerApi.getDashboard(shareToken!);
              if (cancelled) return;
              applyDashboardData(data);
              setPhase('ready');
              return;
            } catch {
              // Token expired or invalid — show PIN form
              shareViewerApi.clearSessionToken(shareToken!);
            }
          }
          setPhase('pin_required');
          return;
        }

        // No PIN — load directly
        const data = await shareViewerApi.getDashboard(shareToken!);
        if (cancelled) return;
        applyDashboardData(data);
        setPhase('ready');
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosErr = err as { response?: { status: number; data?: { expired?: boolean } } };
        if (axiosErr.response?.status === 410 || axiosErr.response?.data?.expired) {
          setPhase('expired');
        } else if (axiosErr.response?.status === 404) {
          setError('This dashboard is no longer available');
          setPhase('error');
        } else {
          setError('Failed to load shared dashboard');
          setPhase('error');
        }
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [shareToken, applyDashboardData]);

  const submitPin = useCallback(
    async (pin: string) => {
      const token = shareTokenRef.current;
      if (!token) return;

      setPinError(null);

      try {
        await shareViewerApi.verifyPin(token, pin);

        const data = await shareViewerApi.getDashboard(token);
        applyDashboardData(data);
        setPhase('ready');
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status: number; data?: { error?: string } } };
        if (axiosErr.response?.status === 429) {
          setPinError('Too many attempts. Try again in 15 minutes.');
        } else if (axiosErr.response?.status === 401) {
          setPinError('Invalid PIN');
        } else if (axiosErr.response?.status === 410) {
          setPhase('expired');
        } else {
          setPinError('Verification failed');
        }
      }
    },
    [applyDashboardData]
  );

  const refreshData = useCallback(async () => {
    const token = shareTokenRef.current;
    if (!token) return;

    setIsRefreshing(true);
    try {
      const cv = buildControlValues(activeControlValues);
      const newDataMap = await shareViewerApi.refreshData(token, cv);

      const updated = new Map<string, Record<string, unknown>[]>();
      for (const [blockId, rows] of Object.entries(newDataMap)) {
        updated.set(blockId, rows);
      }
      setBlockData(prev => new Map([...prev, ...updated]));
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [activeControlValues]);

  const { lastRefreshedAt, isPolling } = useLivePolling({
    dataMode: dashboard?.data_mode as 'static' | 'live' | undefined,
    refreshIntervalSeconds:
      dashboard?.share_settings?.auto_refresh_seconds ??
      dashboard?.refresh_interval,
    refreshFn: refreshData,
    enabled: phase === 'ready' && blocks.length > 0,
  });

  const changeControlValue = useCallback(
    async (parameterKey: string, value: unknown) => {
      const token = shareTokenRef.current;
      if (!token) return;

      const updatedValues = { ...activeControlValues, [parameterKey]: value };
      setActiveControlValues(updatedValues);
      setIsRefreshing(true);

      try {
        const cv = buildControlValues(updatedValues);
        const newDataMap = await shareViewerApi.refreshData(token, cv);

        const updated = new Map<string, Record<string, unknown>[]>();
        for (const [blockId, rows] of Object.entries(newDataMap)) {
          updated.set(blockId, rows);
        }
        setBlockData(prev => new Map([...prev, ...updated]));
      } catch {
        toast.error('Failed to refresh data with new control values');
      } finally {
        setIsRefreshing(false);
      }
    },
    [activeControlValues]
  );

  return {
    phase,
    dashboard,
    blocks,
    blockData,
    controls,
    activeControlValues,
    isRefreshing,
    error,
    pinRequired: phase === 'pin_required',
    pinError,
    renderHandles,
    submitPin,
    changeControlValue,
    refreshData,
    lastRefreshedAt,
    isPolling,
  };
}
